import { useCallback, useEffect, useRef, useState } from 'react'

interface UseVerticalResizeParams {
  initialPercent: number
  minPercent?: number
  maxPercent?: number
}

export function useVerticalResize({
  initialPercent,
  minPercent = 10,
  maxPercent = 90,
}: UseVerticalResizeParams) {
  const [percent, setPercent] = useState(initialPercent)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const startY = useRef(0)
  const startPercent = useRef(0)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return
      const containerHeight = containerRef.current.getBoundingClientRect().height
      if (containerHeight === 0) return
      const deltaY = e.clientY - startY.current
      const deltaPct = (deltaY / containerHeight) * 100
      const newPercent = Math.min(
        maxPercent,
        Math.max(minPercent, startPercent.current + deltaPct)
      )
      setPercent(newPercent)
    },
    [minPercent, maxPercent]
  )

  const handleMouseUp = useCallback(() => {
    isDragging.current = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    document.body.style.webkitUserSelect = ''
  }, [])

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.body.style.webkitUserSelect = ''
    }
  }, [handleMouseMove, handleMouseUp])

  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current = true
      startY.current = e.clientY
      startPercent.current = percent
      document.body.style.cursor = 'row-resize'
      document.body.style.userSelect = 'none'
      document.body.style.webkitUserSelect = 'none'
    },
    [percent]
  )

  return { percent, containerRef, onDragStart }
}
